import FormButton from '@/components/Form/FormButton'
import FormGroup from '@/components/Form/FormGroup'
import FormInput from '@/components/Form/FormInput'
import FormText from '@/components/Form/FormText'
import FormTextRequire from '@/components/Form/FormTextRequire'
import FormView from '@/components/Form/FormView'
import { HeaderLogo } from '@/components/HeaderLogo'
import { LoginSlider } from '@/components/LoginSlider'
import { BannerType } from '@/constants'
import { useAuth } from '@/containers'
import { useCookie } from '@/hooks/useCookie'
import { type BannerList } from '@/types'
import { api } from '@/utils'
import { setContext } from '@/utils/api-interceptors'
import { getBannerSSR } from '@/utils/endpoint'
import { translateSchema } from '@/utils/validations'
import clsx from 'clsx'
import { deleteCookie, getCookies, setCookie } from 'cookies-next'
import { useFormik } from 'formik'
import { type GetServerSideProps } from 'next'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'

type Props = {
  phoneNumber: string | null
  banner: BannerList[]
}

const SearchOrder = (props: Props) => {
  const t = useTranslations('searchOrder')
  const { banner } = props
  const [phoneNumber, setPhoneNumber] = useCookie<string | null>(
    'phoneNumber',
    props.phoneNumber || null
  )
  const [requestId, setRequestId] = useCookie('requestId', '')
  const [remainTime, setRemainTime] = useState(120)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { token, setUser } = useAuth()
  const router = useRouter()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const timeRef = useRef<any>(null)

  const onSubmitFn = () => {
    setIsSubmitted(true)
  }

  useEffect(() => {
    return () => {
      deleteCookie('requestId')
      deleteCookie('phoneNumber')
    }
  }, [])

  const formik = useFormik({
    initialValues: {
      phone: '',
    },
    validationSchema: translateSchema('searchOrder', t),

    onSubmit: async values => {
      if (!window.grecaptcha) {
        console.error('reCAPTCHA not yet loaded')
        return
      }
      const tokenReCaptcha = await window.grecaptcha.execute(
        process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string,
        { action: 'login' }
      )
      if (tokenReCaptcha) {
        setErrorMessage('')
        setIsProcessing(true)
        const res = await api(`api/auth/login/request-otp`, {
          method: 'POST',
          baseURL: process.env.NEXT_PUBLIC_AUTH_API_URL,
          headers: { recaptcha: tokenReCaptcha },
          data: values,
        })
        if (res instanceof Error) {
          setErrorMessage(res.message)
          setIsProcessing(false)
          return
        }
        setPhoneNumber(values.phone)
        setIsProcessing(false)
        setRequestId(res.data.requestId)
      }
    },
  })

  const formikOtp = useFormik({
    initialValues: {
      otp: '',
    },
    validationSchema: translateSchema('searchOrderOtp', t),

    onSubmit: async values => {
      setIsProcessing(true)
      const res = await api(`api/auth/login/otp`, {
        method: 'POST',
        baseURL: process.env.NEXT_PUBLIC_AUTH_API_URL,
        data: {
          code: values.otp.trim(),
          requestId,
        },
      })
      if (res instanceof Error) {
        setIsProcessing(false)
        setErrorMessage(res.message)
        return
      }
      setIsProcessing(false)
      setUser(res.data.profile)
      setCookie('token', res.data.token, { maxAge: 2147483647 })
      setCookie('channel', res.data.profile.saleChannel, { maxAge: 2147483647 })
      deleteCookie('phoneNumber')
      deleteCookie('requestId')
      router.push('/orders')
    },
  })

  const handleResendOtp = async () => {
    setErrorMessage('')
    api(`api/auth/login/request-otp`, {
      method: 'POST',
      baseURL: process.env.NEXT_PUBLIC_AUTH_API_URL,
      data: phoneNumber,
    })
      .then(() => {
        clearInterval(timeRef.current)
        setRemainTime(120)
        timeRef.current = setInterval(() => {
          setRemainTime(prevTime => prevTime - 1)
        }, 1000)
      })
      .catch(error => {
        setErrorMessage(error.message)
      })
  }

  useEffect(() => {
    timeRef.current = setInterval(() => {
      setRemainTime(prev => prev - 1)
    }, 1000)
    return () => {
      clearInterval(timeRef.current)
    }
  }, [])

  useEffect(() => {
    if (remainTime === 0) {
      clearInterval(timeRef.current)
    }
  }, [remainTime])

  if (token) {
    return router.push('/orders')
  }

  return (
    <>
      <style global jsx>{`
        body::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <main>
        <div className='absolute inset-0 z-[-1] h-screen bg-opacity-50'>
          <LoginSlider
            sliders={
              banner.find((e: BannerList) => e.type === BannerType.LOGIN)?.items
            }
          ></LoginSlider>
        </div>
        <div className='flex min-h-screen -translate-y-8 flex-col items-center justify-center'>
          <HeaderLogo />
          <div className='mt-4 w-full max-w-[520px] rounded-lg bg-white p-8 shadow-lg'>
            <div className='flex w-full flex-2 flex-col'>
              <div className='text-center text-HeadlineLarge font-bold text-dark-400'>
                {t('searchOrder')}
              </div>
              <div className='mt-[40px]'>
                {phoneNumber && phoneNumber !== '' ? (
                  <>
                    <div>
                      <FormView
                        onSubmit={formikOtp.handleSubmit}
                        buttonClassName='mt-[24px]'
                      >
                        <FormGroup className='flex w-full flex-col'>
                          <FormText className='mb-[32px] text-left text-Headline font-normal text-grey-800'>
                            {t('otpSent')}{' '}
                            <span className='font-bold'>{phoneNumber}</span>
                          </FormText>
                          <FormInput
                            name='otp'
                            placeholder={t('otpPld')}
                            value={formikOtp.values.otp.trim()}
                            isError={isSubmitted && formikOtp.errors.otp}
                            onChange={e => {
                              formikOtp.handleChange({
                                target: {
                                  value: e.target.value.trim(),
                                  name: 'otp',
                                },
                              })
                              setErrorMessage('')
                            }}
                            alert
                            className={clsx(
                              formikOtp.errors.otp && 'border border-red-600',
                              '!h-14 w-[380px] min-w-[360px] rounded-lg border border-solid border-info-border text-left transition placeholder:text-dark-400/70 hover:cursor-pointer'
                            )}
                          />
                          {isSubmitted && formikOtp.errors.otp ? (
                            <FormTextRequire className='pt-2 text-sm font-normal text-red-500'>
                              {formikOtp.errors.otp}
                            </FormTextRequire>
                          ) : null}
                          {errorMessage && isSubmitted ? (
                            <FormTextRequire className='pt-2 text-sm font-normal text-red-500'>
                              {errorMessage}
                            </FormTextRequire>
                          ) : null}
                        </FormGroup>
                        <FormButton
                          className={clsx(
                            formikOtp.dirty &&
                              ' !bg-primary text-Headline  !text-white-400',
                            'disabled:text-[rgb(0 0 0 / 25%)] h-14 w-full rounded-lg border bg-grey p-[10px] font-semibold uppercase text-black-400/25 disabled:bg-grey disabled:text-black-400/25 disabled:outline-none'
                          )}
                          onClick={onSubmitFn}
                          isProcessing={isProcessing}
                          disabled={!formikOtp.dirty}
                        >
                          {t('requestOtp')}
                        </FormButton>
                      </FormView>
                      {remainTime === 0 ? (
                        <span className='mt-[32px] block text-center text-fieldLabel'>
                          <span className=' text-dark-400'>
                            {t('dontReceive')},{' '}
                          </span>
                          <span
                            className='cursor-pointer text-fieldLabel text-blue'
                            onClick={handleResendOtp}
                          >
                            {t('resend')}
                          </span>
                        </span>
                      ) : (
                        <div className='mt-[20px] text-center'>
                          {t('resendDesc')}{' '}
                          <span>
                            {remainTime} {t('second')}
                          </span>
                        </div>
                      )}
                      <span
                        onClick={() => {
                          setPhoneNumber('')
                          clearInterval(timeRef.current)
                          setRemainTime(120)
                          timeRef.current = setInterval(() => {
                            setRemainTime(prevTime => prevTime - 1)
                          }, 1000)
                        }}
                        className='mt-[24px] block cursor-pointer text-center text-fieldLabel font-normal text-blue'
                      >
                        {t('changePhone')}
                      </span>
                    </div>
                  </>
                ) : (
                  <FormView
                    onSubmit={formik.handleSubmit}
                    buttonClassName='mt-[24px]'
                  >
                    <FormGroup className='flex w-full flex-col'>
                      <FormText className='mb-1 text-left text-fieldInput font-bold'>
                        {t('phone')}
                      </FormText>
                      <FormInput
                        name='phone'
                        placeholder={t('phonePld')}
                        value={formik.values.phone.trim()}
                        isError={isSubmitted && formik.errors.phone}
                        onChange={e => {
                          formik.handleChange({
                            target: {
                              value: e.target.value.trim().replace(/\D/, ''),
                              name: 'phone',
                            },
                          })
                          setErrorMessage('')
                        }}
                        alert
                        className={clsx(
                          isSubmitted &&
                            formik.errors.phone &&
                            'border border-red-600',
                          ' !h-14 w-[380px] min-w-[360px] rounded-lg border border-solid border-grey-500 text-left text-fieldInput transition placeholder:text-dark-400/70  placeholder:opacity-70 hover:cursor-pointer'
                        )}
                      />
                      {isSubmitted && formik.errors.phone ? (
                        <FormTextRequire className='pt-2 text-sm font-normal text-red-500'>
                          {formik.errors.phone}
                        </FormTextRequire>
                      ) : null}
                      {errorMessage && isSubmitted ? (
                        <FormTextRequire className='border-none pl-0 pt-2 text-sm  text-red-500'>
                          {errorMessage}
                        </FormTextRequire>
                      ) : null}
                    </FormGroup>
                    <FormButton
                      className={clsx(
                        formik.dirty &&
                          ' !bg-primary text-Headline  !text-white-400',
                        'disabled:text-[rgb(0 0 0 / 25%)] h-14 w-full rounded border bg-grey p-[10px] font-semibold uppercase text-black-400/25 disabled:bg-grey disabled:text-black-400/25 disabled:outline-none'
                      )}
                      onClick={onSubmitFn}
                      isProcessing={isProcessing}
                      disabled={isProcessing}
                    >
                      {t('requestOtp')}
                    </FormButton>
                  </FormView>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}

export default SearchOrder

export const getServerSideProps: GetServerSideProps = async ({
  req,
  res,
  locale,
}) => {
  const { token, phoneNumber, language, messages } = await setContext(
    getCookies({ req, res }),
    {
      language: locale,
    }
  )

  if (token) {
    return {
      redirect: {
        permanent: false,
        destination: `/orders`,
      },
    }
  }
  const [banner] = await Promise.all([getBannerSSR()])

  return {
    props: {
      messages,
      language,
      phoneNumber: phoneNumber || null,
      token: token || null,
      banner,
    },
  }
}
